import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Banner, Button, useToast } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';
import { billingErrorDetails } from '../../shared/api/billing.js';
import { billingError } from './billingText.js';
import { useCompleteCardSetup } from './prepaidHooks.js';

/**
 * Back from Stripe's card setup (doc 59 F01): Settings › Billing, or the
 * wizard's Activate step while in setup (`createBillingPaymentSetup`). The server links the card even
 * if this page never loads (webhook + reconciler); this is the fast path.
 * Handled once PER SESSION ID; the URL is cleaned only on a definite answer —
 * a transient failure keeps it and offers a retry, so nobody re-enters a card.
 */
export function useCardSetupReturn(businessId: string): { retrying: boolean; failed: boolean; retry: () => void } {
  const { t } = useI18n();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const complete = useCompleteCardSetup(businessId);
  const handled = useRef(new Set<string>());
  const [failed, setFailed] = useState(false);
  const sessionId = params.get('setup_session');
  const clean = () => {
    const next = new URLSearchParams(params);
    next.delete('setup_session');
    setParams(next, { replace: true });
  };
  const run = (id: string) => {
    setFailed(false);
    complete.mutate(id, {
      onSuccess: () => {
        toast({ title: t('prepaid.card.saved'), tone: 'success' });
        clean();
      },
      onError: (e) => {
        const { code } = billingErrorDetails(e);
        const definite = code === 'validation' || (e as { code?: string })?.code === 'functions/permission-denied';
        if (definite) {
          toast({ title: billingError(t, e), tone: 'danger' });
          clean();
        } else {
          // Pending at Stripe, or a network blip: keep the return, let the
          // owner retry; the server will also finish it on its own.
          setFailed(true);
        }
      },
    });
  };
  useEffect(() => {
    if (!sessionId || handled.current.has(`${businessId}:${sessionId}`)) return;
    handled.current.add(`${businessId}:${sessionId}`);
    run(sessionId);
  }, [sessionId, businessId]); // eslint-disable-line react-hooks/exhaustive-deps
  return { retrying: complete.isPending, failed: failed && !!sessionId, retry: () => sessionId && run(sessionId) };
}

/** Stripe has not confirmed the new card yet: say so, offer a retry. */
export function CardSetupPending({ setup }: { setup: ReturnType<typeof useCardSetupReturn> }) {
  const { t } = useI18n();
  if (!setup.failed) return null;
  return (
    <Banner
      tone="warning"
      title={t('plan.cardSetup.pendingTitle')}
      actions={<Button variant="secondary" loading={setup.retrying} onClick={setup.retry}>{t('prepaid.retry')}</Button>}
    >
      {t('plan.cardSetup.pendingBody')}
    </Banner>
  );
}
